import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { H3, IconSpinner, NamedLink } from '../../../../components';
import {
  countsAsPaid,
  PAYMENT_METHODS,
  SUBSCRIPTION_STATUS,
} from '../../../../constants/payment.constants';
import { USER_TYPE } from '../../../../constants/user.constants';
import { fetchCurrentUser } from '../../../../ducks/user.duck';
import { INVALID_PROMOTION_CODE_ERROR } from '../../../../constants/errors.constants';
import { fetchPaymentStatus, PAYMENT_STATUS } from '../../../PaymentReturnPage/PaymentReturnPage.duck';
import CreateListingDurationForm from '../../../CreateListingDurationPage/CreateListingDurationForm';
import { DETAILS } from '../EditListingWizardTab';
import PaymentMethodsSubscriptionActive from '../../../PaymentMethodsPage/PaymentMethodsSubscriptionActive';
import {
  fetchSubscriptionDetails,
  subscribe,
  subscribeReset,
} from '../../../PaymentMethodsPage/PaymentMethodsPage.duck';
import useBfcacheRestore from '../../../../hooks/useBfcacheRestore';

import css from './EditListingSubscriptionPlan.module.css';

/**
 * Known error codes from POST user/subscription/subscribe, mapped to copy the
 * seller can act on. Anything else falls back to the server's own message, then
 * to a generic line — a failed payment attempt must never look like nothing
 * happened.
 */
const ERROR_CODE_TRANSLATIONS = {
  SUBSCRIPTION_EXISTS_FOR_LISTING: 'EditListingSubscriptionPlan.alreadyPaidError',
  FLOATING_SUBSCRIPTION_EXISTS: 'SubscriptionDetailsModal.floatingSubscriptionError',
  SUBSCRIPTION_PROCESSING: 'SubscriptionDetailsModal.subscriptionProcessingError',
};

const getErrorText = (error, intl) => {
  if (!error) return null;

  const data = error?.response?.data;
  const translationId = data?.code && ERROR_CODE_TRANSLATIONS[data.code];
  if (translationId) {
    return intl.formatMessage({ id: translationId });
  }

  const message = Array.isArray(data?.message) ? data.message.join(', ') : data?.message;
  if (message === INVALID_PROMOTION_CODE_ERROR) {
    return intl.formatMessage({ id: 'PaymentMethodsPage.invalidPromotionCode' });
  }

  const status = error?.response?.status;
  if (status && status < 500 && message) {
    return message;
  }

  return intl.formatMessage({ id: 'EditListingSubscriptionPlan.genericError' });
};

/**
 * The payment step of the listing wizard.
 *
 * Pays for *this* listing: the draft already exists by the time the seller gets
 * here, so the subscription is attached to a real advertisement from the moment
 * Stripe creates it. That is what makes a subscription belonging to nothing
 * impossible.
 */
const EditListingSubscriptionPlan = props => {
  const { className, listing, params, currentUser, changeSubscriptionURL } = props;

  const intl = useIntl();
  const dispatch = useDispatch();

  const listingId = listing?.id?.uuid;
  const { userType } = currentUser?.attributes?.profile?.publicData || {};
  const isPrivateSeller = userType === USER_TYPE.PRIVATE_SELLER;

  const {
    subscriptionDetails,
    subscriptionDetailsInProgress,
    subscribingInProgress,
    subscribingError,
  } = useSelector(state => state.PaymentMethodsPage);

  useEffect(() => {
    dispatch(fetchSubscriptionDetails());
  }, [dispatch, listingId]);

  const listingSubscription =
    subscriptionDetails?.listings?.find(l => l.listingId === listingId) || null;

  /**
   * Is this advertisement actually paid for?
   *
   * Not the same question as "is there a subscription record". Stripe fires
   * `customer.subscription.created` as soon as a checkout attempt produces a
   * subscription — including one that is `incomplete` because the seller pressed back
   * or the card was declined. We record that as `inactive`, so a *failed* attempt
   * leaves behind a record that looks, from metadata alone, exactly like a paid one.
   *
   * `active` is unambiguous. `inactive` is not: it is either SEPA money still in
   * flight (paid, keep the seller out of the form) or a dead attempt (let them pay).
   * Only Stripe can tell those apart, so we ask it — which is what the payment-status
   * endpoint exists for, and it promotes the record to `active` as a side effect when
   * the answer is good.
   */
  const isConfirmedPaid = countsAsPaid(listingSubscription?.state);
  const isAmbiguous = listingSubscription?.state === SUBSCRIPTION_STATUS.INACTIVE;

  const [resolvedStatus, setResolvedStatus] = useState(null);
  const [isResolving, setIsResolving] = useState(false);

  useEffect(() => {
    if (!isAmbiguous || !listingId) {
      setResolvedStatus(null);
      return undefined;
    }

    let cancelled = false;
    setIsResolving(true);

    // The shared thunk rather than the API client directly: when the answer is good it
    // also refetches the current user, and the wizard reads the subscription off that
    // to decide whether this step is done. Without the refresh the seller would be told
    // they had paid while the sidebar still showed the step outstanding and the details
    // step stayed locked.
    dispatch(fetchPaymentStatus(listingId))
      .then(data => {
        // A null result means the request failed. Treat it as unpaid so the seller is
        // not stranded — the API guard is the real backstop and refuses a genuine
        // double payment.
        if (!cancelled) setResolvedStatus(data?.status ?? PAYMENT_STATUS.NONE);
      })
      .finally(() => {
        if (!cancelled) setIsResolving(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, isAmbiguous, listingId]);

  const isPaidPerStripe =
    resolvedStatus === PAYMENT_STATUS.PAID || resolvedStatus === PAYMENT_STATUS.PROCESSING;
  const showAlreadyPaid = isConfirmedPaid || isPaidPerStripe;

  /**
   * Is the money still on its way, rather than collected?
   *
   * The one place the two paid states are told apart — everywhere else treats them
   * identically. Here the seller is reading a screen about their payment, so
   * "collected" and "on its way" are different things to say.
   *
   * Both sources are consulted on purpose. A seller who gets back here before
   * `payment_intent.processing` has landed still has `inactive` in our metadata while
   * Stripe already knows the direct debit is in flight; reading only the stored state
   * would tell exactly that seller their money had been collected when it has not.
   */
  const isAwaitingCollection =
    listingSubscription?.state === SUBSCRIPTION_STATUS.PROCESSING ||
    resolvedStatus === PAYMENT_STATUS.PROCESSING;

  /**
   * Two sources disagree, and the seller gets stuck in the gap.
   *
   * This panel reads the subscription straight from the API; the wizard reads it off
   * `currentUser` metadata to decide whether this step is done and whether the details
   * step is unlocked. Right after paying, the first is fresh and the second is not — so
   * the seller is told the advertisement is paid for while the sidebar still shows the
   * step outstanding and every step after it stays locked.
   *
   * Refetch the user when they disagree, so the wizard catches up.
   */
  const wizardSeesPayment = countsAsPaid(
    currentUser?.attributes?.profile?.metadata?.subscription?.listings?.find(
      l => l.listingId === listingId
    )?.state
  );

  useEffect(() => {
    if (showAlreadyPaid && !wizardSeesPayment) {
      dispatch(fetchCurrentUser({ enforce: true }));
    }
  }, [dispatch, showAlreadyPaid, wizardSeesPayment]);

  const initialValues = {
    plan: isPrivateSeller ? PAYMENT_METHODS.PRIVATE.QUARTERLY : PAYMENT_METHODS.BUSINESS.BASIC,
  };

  // Sending listingId is the whole point: it puts the listing on the checkout
  // session and on the subscription's metadata, so the return URLs and the
  // webhook both know which advertisement was paid for.
  const handleSubmit = values => dispatch(subscribe({ ...values, listingId }));

  const errorText = getErrorText(subscribingError, intl);

  // Back from Stripe restores this page with `subscribingInProgress` still true — it is
  // left set on purpose so the button cannot be clicked twice mid-redirect — which
  // would leave the seller unable to pay at all. Release it.
  useBfcacheRestore(useCallback(() => dispatch(subscribeReset()), [dispatch]));

  // No payment form while we might be confirming a payment — the rule that keeps a
  // seller from paying twice because the answer had not arrived yet.
  if ((subscriptionDetailsInProgress && !subscriptionDetails) || isResolving) {
    return (
      <div className={className}>
        <IconSpinner />
      </div>
    );
  }

  return (
    <div className={className}>
      {showAlreadyPaid ? (
        <>
          {/* Heading and body have to agree. "Het betalingsproces succesvol afgerond"
              over a line saying the money will be collected in the coming days reads
              as a contradiction, and the half a seller believes is the reassuring one.
              A direct debit that has not been collected is in behandeling, not done. */}
          <H3 as="h1">
            <FormattedMessage
              id={
                isAwaitingCollection
                  ? 'EditListingSubscriptionPlan.processingTitle'
                  : 'EditListingSubscriptionPlan.alreadyPaidTitle'
              }
            />
          </H3>
          {/* The client's line describes direct debit — money checked and collected
              over the coming days — which is only true for SEPA. A card payer has
              already been charged, so they get a settled wording instead. */}
          <p className={css.alreadyPaidBody}>
            <FormattedMessage
              id={
                isAwaitingCollection
                  ? 'EditListingSubscriptionPlan.alreadyPaidBody'
                  : 'EditListingSubscriptionPlan.alreadyPaidBodyCard'
              }
            />
          </p>
          <PaymentMethodsSubscriptionActive
            subscription={listingSubscription}
            url={changeSubscriptionURL}
          />
          {/* Paying is the middle of the flow, not the end of it. Without this the
              seller is told the advertisement is paid for and left with nowhere to go —
              the rest of the wizard is only reachable from the sidebar, which is not
              obviously a way forward. */}
          {params ? (
            <NamedLink
              name="EditListingPage"
              params={{ ...params, tab: DETAILS }}
              className={css.continueButton}
            >
              <FormattedMessage id="EditListingSubscriptionPlan.continue" />
            </NamedLink>
          ) : null}
        </>
      ) : (
        <>
          <CreateListingDurationForm
            initialValues={initialValues}
            onSubmit={handleSubmit}
            inProgress={subscribingInProgress}
            stepIndicator={
              <FormattedMessage
                id="EditListingSubscriptionPlan.stepIndicator"
                values={{ step: 2, total: 4 }}
              />
            }
          />
          {errorText && <span className={css.error}>{errorText}</span>}
        </>
      )}
    </div>
  );
};

export default EditListingSubscriptionPlan;
